import * as React from 'react';

import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useIntl } from 'react-intl';

import { useSendReservationConfirmation } from 'src/features/reservations/hooks/useSendReservationConfirmation';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { ReservationsVehicleStayList } from 'src/features/reservations/types/reservationType';

interface SendConfirmationModalProps extends React.HTMLAttributes<HTMLDivElement> {
    selectedReservation?: ReservationsVehicleStayList;
}

const SendConfirmationModal = ({ selectedReservation, children }: SendConfirmationModalProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const client_email = selectedReservation?.client_email;
    const reservationNumber = selectedReservation?.number;

    const { mutate: sendReservationConfirmation } = useSendReservationConfirmation({
        onSuccess: () => {
            setIsModalOpen(!isModalOpen);
            toast({
                title: formatMessage(reservationMessages.confirmationSentSuccess),
                description: formatMessage(reservationMessages.confirmationSentSuccessDetails, {
                    client_email,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faPaperPlane} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.sendConfirmation)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.sendConfirmationDescription, { reservationNumber })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(reservationMessages.sendConfirmationTo, { client_email })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={() => {
                            if (selectedReservation) {
                                void sendReservationConfirmation({ parkingId, reservationId: selectedReservation.id });
                            }
                        }}
                        className="flex-1"
                    >
                        {formatMessage(generalMessages.send)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SendConfirmationModal;
